import cx from "classnames";
import { useEffect, useState } from "react";
import { faArrowRight, faInfoCircle, faFilter } from "@fortawesome/pro-regular-svg-icons";

import { RebateStatus } from "common/types/rebates";
import { parseNumber } from "common/utils/numbers";
import { useMatch } from "common/router/hooks";
import Icon from "common/components/Icon";
import Spinner from "common/components/Spinner";
import { useListSearchState } from "common/hooks";
import { Feature } from "common/types";

import Page from "client/common/components/layouts/Page";
import Container from "client/common/components/layouts/Container";
import Pagination from "client/common/components/layouts/Pagination";
import Results from "client/common/components/results/Results";
import Body from "client/common/components/layouts/Body";
import FetchErrorPage from "client/common/components/errors/FetchErrorPage";
import LoadingPage from "client/common/components/loading/LoadingPage";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import PillButton from "client/common/components/buttons/PillButton";
import { useClinic, useFeatures } from "client/clinics/hooks";
import { Paths } from "client/routes";
import {
  ItemClickSource,
  ProductListFilterSurface,
  ProductListSource,
} from "client/analytics/types";
import { useItemsViewOnVisible } from "client/analytics/utils";
import HomeBrowseV1, { CategoryCarousel } from "client/home/components/HomeBrowseV1";
import HomeBrowseV2, { CategoryCarouselV2 } from "client/home/components/HomeBrowseV2";

import {
  EMPTY_PRODUCT_SELECTION,
  getPromoCodeMessagePrefix,
  getPromoCodePunctuation,
  getRebateProductFiltersFromSearch,
  partitionCarousels,
  toPromotionDateRange,
} from "../utils";
import { BrowseAllPatientKind, ProductSearchFilters, SelectedProductFilters } from "../types";
import { DEFAULT_PAGE_SIZE } from "../constants/filterDefinitions";
import {
  useBrowseQuery,
  useCarouselDisplayQuery,
  useRebateItemsQuery,
  useFilterPanel,
  useRebateQuery,
} from "../hooks";
import { useProductFilterDefinitions } from "../hooks/filterDefinitions";
import PromotionItemCard from "./PromotionItemCard";
import PreFooter from "./PreFooter";
import FilterPanelModal from "./FilterPanelModal";
import FilterLayout from "./FilterLayout";
import FilterPanel from "./FilterPanel";
import SearchCount from "./SearchCount";
import AppliedFilterTags from "./AppliedFilterTags";
import { useRebateDrawer } from "../stackables";
import { INITIAL_CLIENT_DATA } from "../../common/utils";

import styles from "../styles/RebatePage.module.less";

/**
 * This detail page is primarily meant for promotional emails etc sent out by clinics.
 * Nowhere in the client app currently links to it, but the urls will be accessed directly.
 */
const RebatePage = () => {
  const clinic = useClinic();
  const features = useFeatures();
  const isCarouselV2 = features[Feature.CarouselV2];
  const HomeBrowse = isCarouselV2 ? HomeBrowseV2 : HomeBrowseV1;

  const initialSearch = {
    ...EMPTY_PRODUCT_SELECTION,
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
  };

  const match = useMatch<Paths["products"]["rebate"]>();
  const parsedId = parseNumber(match.params.id, 0);
  const [search, updateSearch] = useListSearchState<Paths["products"]["rebate"]>(initialSearch);
  const [isFiltering, setIsFiltering] = useState(false);
  const filters = getRebateProductFiltersFromSearch(search);
  const { open: openRebateDrawer } = useRebateDrawer();
  const { is_staff: isStaff } = INITIAL_CLIENT_DATA;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [search.page]);

  const {
    data: rebate,
    error: rebateError,
    isFetching: isRebateFetching,
    isLoading: isRebateLoading,
  } = useRebateQuery(parsedId);

  const {
    data: items,
    error: itemsError,
    isFetching: areItemsFetching,
    isLoading: areItemsLoading,
  } = useRebateItemsQuery(parsedId, {
    ...filters,
    page_size: DEFAULT_PAGE_SIZE,
    page: parseNumber(search.page, 1),
  });
  const definitions = useProductFilterDefinitions();
  const panel = useFilterPanel<SelectedProductFilters, ProductSearchFilters>({
    definitions,
    response: items,
    filters,
    onChange: updateSearch,
    listSource: ProductListSource.RebatePage,
  });
  const { filterOptions, showFilterPanel, hasSelectedFilters } = panel;

  const itemsViewRef = useItemsViewOnVisible(items?.results, ItemClickSource.Rebate);

  const {
    data: browse,
    isLoading: isBrowseLoading,
    error: browseError,
  } = useBrowseQuery({ species: BrowseAllPatientKind.ALL }, { enabled: !isCarouselV2 });
  const {
    data: carouselDisplay,
    isLoading: isCarouselDisplayLoading,
    error: carouselDisplayError,
  } = useCarouselDisplayQuery({ species: BrowseAllPatientKind.ALL }, { enabled: isCarouselV2 });

  const renderContent = () => {
    if (areItemsLoading) {
      return <Results.Loading description="Loading rebate items" />;
    }

    if (!items || itemsError) {
      return <Results.FetchError error={rebateError} entityName="rebate item" />;
    }

    return (
      <FilterLayout panel={showFilterPanel && <FilterPanel.Rail {...panel} />}>
        <div className={styles.content}>
          <div className={styles.info}>
            <div className={styles.label}>
              {isRebateFetching || areItemsFetching ? (
                <Spinner />
              ) : (
                <SearchCount paginator={items.paginator} entityName="eligible item" />
              )}
            </div>
            {filterOptions && !showFilterPanel && (
              <AppliedFilterTags<SelectedProductFilters, ProductSearchFilters>
                definitions={definitions}
                options={filterOptions}
                value={filters}
                onChange={panel.changeFrom(ProductListFilterSurface.AppliedFilters)}
              />
            )}
            {!showFilterPanel && (
              <PillButton
                variant="secondary"
                size="s"
                type="button"
                onClick={() => setIsFiltering(true)}
              >
                <span className={styles.filter}>
                  <Icon icon={faFilter} /> Filter
                </span>
              </PillButton>
            )}
          </div>
          <FilterPanel.AppliedRow {...panel} />
          {filterOptions && !showFilterPanel && (
            <FilterPanelModal
              definitions={definitions}
              options={filterOptions}
              isVisible={isFiltering}
              onClose={() => setIsFiltering(false)}
              value={filters}
              onChange={panel.changeFrom(ProductListFilterSurface.Drawer)}
            />
          )}
          {items.results.length ? (
            <div className={styles.search}>
              <div
                ref={itemsViewRef}
                className={cx(styles.products, showFilterPanel && styles.productsNarrow)}
              >
                {items.results.map((item) => (
                  <PromotionItemCard
                    key={item.id}
                    classNames={{
                      product: styles.product,
                      content: styles.content,
                      image: styles.image,
                      facts: styles.facts,
                      names: styles.names,
                      name: styles.name,
                      description: styles.description,
                    }}
                    item={item}
                    clickSource={ItemClickSource.Rebate}
                  />
                ))}
              </div>
              <Pagination
                paginator={items.paginator}
                onPageChange={(page) => updateSearch({ page })}
                entityName="item"
              />
            </div>
          ) : (
            <Results.None
              header="No items found"
              description={
                hasSelectedFilters ? "No items match the filters you have applied." : undefined
              }
            />
          )}
        </div>
      </FilterLayout>
    );
  };

  const renderCarousel = () => {
    if (isCarouselV2) {
      if (isCarouselDisplayLoading) {
        return <LoadingContainer />;
      }
      if (carouselDisplayError || !carouselDisplay) {
        return null;
      }
      return (
        <CategoryCarouselV2
          carousels={partitionCarousels(carouselDisplay.carousels).categoryCarousels}
        />
      );
    }
    if (isBrowseLoading) {
      return <LoadingContainer />;
    }
    if (browseError || !browse) {
      return null;
    }
    return <CategoryCarousel categories={browse.categories} />;
  };

  if (isRebateLoading) {
    return <LoadingPage />;
  }

  if (!rebate || rebateError) {
    return <FetchErrorPage error={rebateError} entityName="rebate" />;
  }

  const styledPromoCodes = () => {
    const codes = rebate.promo_codes.map((promo) => promo.code);
    const codePunctuation = getPromoCodePunctuation(codes);
    return codes.map((code) => {
      return (
        <div className={styles.promoCodeContainer} key={code}>
          {codePunctuation[code]}
          <div className={styles.promoCode}>{code}</div>
        </div>
      );
    });
  };

  if (
    rebate.status !== RebateStatus.Active &&
    !(rebate.status === RebateStatus.Previewing && isStaff)
  ) {
    const errorMessage = `This ${rebate.promo_codes.length ? "promo code" : "discount"} ${
      rebate.status === RebateStatus.Expired ? "has expired" : "is not active"
    }`;

    return (
      <Page>
        <div className={styles.gradient} />
        <Container>
          <div className={styles.top}>
            <div className={styles.titleColumnContainer}>
              <div className={styles.title}>Uh oh! {errorMessage}...</div>
              <div className={styles.subtitle}>
                You can still browse products for your furry friend. Shop preventatives, diet, and
                medications below!
              </div>
            </div>
          </div>
        </Container>
        <Body className={styles.main}>
          <HomeBrowse clinic={clinic} />
        </Body>
      </Page>
    );
  }

  const renderPromoCodeMessage = () => {
    const messagePrefix = getPromoCodeMessagePrefix(rebate.promo_message);
    return (
      <p className={styles.promoCodeMessage}>
        {messagePrefix} Use promo code {styledPromoCodes()} at checkout.
      </p>
    );
  };

  return (
    <Page>
      {!!rebate.banner_image_url && (
        <div
          className={styles.background}
          style={{ backgroundImage: `url(${rebate.banner_image_url})` }}
        />
      )}
      <div className={styles.gradient} style={{ opacity: rebate.banner_image_url ? 0.5 : 1 }} />
      <Container>
        <div className={styles.top}>
          <div className={styles.heading}>
            <div className={styles.expiration}>
              {toPromotionDateRange(rebate.start_date, rebate.end_date)}
            </div>
            <div className={styles.titleRowContainer}>
              <div className={styles.title}>{rebate.promo_title}</div>
              <PillButton
                className={styles.button}
                variant="primary"
                onClick={() => openRebateDrawer(rebate)}
              >
                <div className={styles.buttonContent}>
                  <Icon icon={faInfoCircle} />
                  <div>View details</div>
                  <Icon className={styles.angleRight} icon={faArrowRight} />
                </div>
              </PillButton>
            </div>
            <div className={styles.subtitle}>
              {rebate.promo_message}
              {!!rebate.promo_codes.length && renderPromoCodeMessage()}
            </div>
          </div>
        </div>
      </Container>
      <div>
        <Body className={styles.main}>{renderContent()}</Body>
        <Body className={styles.browseBody}>
          <div className={styles.browse}>
            <h3 className={styles.browseTitle}>
              Browse our online store{" "}
              <span className={styles.accent}>& explore other top veterinarian picks</span>
            </h3>
            {renderCarousel()}
          </div>
        </Body>
      </div>
      <PreFooter image={rebate.prefooter_image_url || clinic.storefront_prefooter_image} />
    </Page>
  );
};

export default RebatePage;
